import React from 'react';

/* ── Orden canónico de los 16 eventos Samsung ── */
const SAMSUNG_EVENTS_ORDER = [
  { key: 'cita_programada_terminal',         label: 'Cita programada terminal' },
  { key: 'hora_maxima_salida',               label: 'Hora máxima de salida' },
  { key: 'cita_programada',                  label: 'Cita programada' },
  { key: 'ingreso_bodega_carga',             label: 'Ingreso bodega de carga' },
  { key: 'reporte_centro_carga',             label: 'Reporte centro de carga' },
  { key: 'asignacion_cortina_centro_carga',  label: 'Asignación cortina' },
  { key: 'inicio_de_carga',                  label: 'Inicio de carga' },
  { key: 'fin_de_carga',                     label: 'Fin de carga' },
  { key: 'entrega_documentos_control_tower', label: 'Entrega docs. Control Tower' },
  { key: 'inicio_ruta',                      label: 'Inicio de ruta' },
  { key: 'arribo_cliente',                   label: 'Arribo a cliente' },
  { key: 'entrega_documentos',               label: 'Entrega de documentos' },
  { key: 'asignacion_rampa',                 label: 'Asignación de rampa' },
  { key: 'inicio_de_descarga',               label: 'Inicio de descarga' },
  { key: 'fin_de_descarga',                  label: 'Fin de descarga' },
  { key: 'entrega_de_pods',                  label: 'Entrega de PODs' },
];

/* Convierte el objeto plano {key: timestamp|null} en lista ordenada con status */
const buildEventList = (eventosObj = {}) => {
  let activeAssigned = false;
  return SAMSUNG_EVENTS_ORDER.map(({ key, label }) => {
    const ts = eventosObj[key] ?? null;
    let status;
    if (ts !== null) {
      status = 'completed';
    } else if (!activeAssigned) {
      activeAssigned = true;
      status = 'active';
    } else {
      status = 'pending';
    }
    return { key, label, timestamp: ts, status };
  });
};

const deriveEstado = (eventos) => {
  const completed = eventos.filter(e => e.status === 'completed').length;
  if (completed === 0)              return 'Pendiente';
  if (completed === eventos.length) return 'Completado';
  return 'En proceso';
};

/* ── Status dot SVGs ── */
const DotCompleted = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <circle cx="6" cy="6" r="6" fill="#22c55e" />
    <path d="M3.5 6l1.8 1.8 3.2-3.6" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DotActive = () => (
  <span className="stl-dot-active">
    <span className="stl-dot-active__ring" />
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <circle cx="6" cy="6" r="6" fill="#467fcf" />
      <circle cx="6" cy="6" r="2.5" fill="#fff" />
    </svg>
  </span>
);

const DotPending = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <circle cx="6" cy="6" r="5" stroke="#c4d3e8" strokeWidth="1.5" fill="#f3f6fa" />
  </svg>
);

const STATUS_DOT = { completed: <DotCompleted />, active: <DotActive />, pending: <DotPending /> };
const STATUS_ROW = { completed: 'stl-row--completed', active: 'stl-row--active', pending: 'stl-row--pending' };

const ESTADO_PILL = {
  'Completado': { bg: '#dcfce7', color: '#166534' },
  'En proceso': { bg: '#fef3c7', color: '#92400e' },
  'Pendiente':  { bg: '#f3f6fa', color: '#495057' },
};

const fmtTs = (ts) => {
  if (!ts) return { date: '', time: '' };
  const d = new Date(ts.replace(' ', 'T'));
  if (isNaN(d)) return { date: ts, time: '' };
  const date = d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
  const time = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
  return { date, time };
};

/* ── Componente principal ── */
const ServiceTimeline = ({ po, plataforma, eventosObj = {} }) => {
  const eventos  = buildEventList(eventosObj);
  const estado   = deriveEstado(eventos);
  const completed = eventos.filter(e => e.status === 'completed').length;
  const total     = eventos.length;
  const pct       = Math.round((completed / total) * 100);
  const pill      = ESTADO_PILL[estado] ?? ESTADO_PILL['Pendiente'];

  return (
    <div className="stl-wrapper">
      {/* Header */}
      <div className="stl-header">
        <div className="stl-header-top">
          <span className="stl-chip">📦 {plataforma ?? 'SERVICIO'}</span>
          <span className="stl-service-id">PO {po}</span>
          <span className="stl-status-pill" style={{ background: pill.bg, color: pill.color }}>
            {estado}
          </span>
        </div>
      </div>

      {/* Timeline */}
      <div className="stl-timeline">
        {eventos.map((ev, i) => {
          const isLast = i === eventos.length - 1;
          const { date, time } = fmtTs(ev.timestamp);
          return (
            <div key={ev.key} className={`stl-row ${STATUS_ROW[ev.status]}`}>
              <div className="stl-ts">
                {date && <span className="stl-ts__date">{date}</span>}
                {time && <span className="stl-ts__time">{time}</span>}
              </div>
              <div className="stl-connector">
                {!isLast && <div className="stl-line" />}
                <div className="stl-dot">{STATUS_DOT[ev.status]}</div>
              </div>
              <div className="stl-content">
                <span className="stl-label">{ev.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="stl-footer">
        <div className="stl-progress-track">
          <div className="stl-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="stl-footer-meta">
          <span>{completed}/{total} eventos completados</span>
        </div>
      </div>
    </div>
  );
};

/* ── Skeleton de carga ── */
export const ServiceTimelineSkeleton = ({ po }) => (
  <div className="stl-wrapper stl-skeleton">
    <div className="stl-header">
      <div className="stl-header-top">
        <span className="stl-chip">📦 SERVICIO</span>
        <span className="stl-service-id">PO {po}</span>
      </div>
    </div>
    <div className="stl-skeleton-rows">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="stl-skeleton-row">
          <div className="stl-skeleton-ts" />
          <div className="stl-skeleton-dot" />
          <div className="stl-skeleton-text" style={{ width: `${55 + (i % 3) * 15}%` }} />
        </div>
      ))}
    </div>
    <div className="stl-skeleton-bar" />
  </div>
);

export default ServiceTimeline;
